import { gql } from '@apollo/client';

/**
 * Career Center — LMS student surface (spec 0018, increment A).
 *
 * Operation names are deliberately distinct from the backend field names
 * (`MyCareerCenterInfo` vs `myCareerCenter`, `CareerLessonsList` vs
 * `careerLessons`, `CareerLessonDetail` vs `careerLesson`) — equal names make
 * codegen collapse the generated result type into the schema type
 * (graphql-codegen skill).
 */

/**
 * Membership + overall progress. Returns `null` when the student is NOT
 * enrolled — that null IS the membership check (spec 0018 §3.1), not an error.
 * Everything in the section (menu entry, route, lesson list) gates on it.
 */
export const MyCareerCenterInfoQuery = gql`
  query MyCareerCenterInfo {
    myCareerCenter {
      isPreview
      memberId
      status
      directionTitle
      # ТЗ 0039 доводка UX: id направления нужен, чтобы лента вакансий по
      # умолчанию показывала то, чему человек учится. Поле было на сервере с
      # самого начала, фронт его просто не запрашивал.
      directionId
      directionSource
      enrolledAt
      lessonsCompleted
      lessonsTotal
    }
  }
`;

/**
 * Published lessons with per-viewer lock state; `[]` for non-members.
 * Deliberately does NOT select `content` / `videoUrl` / `attachments`: the list
 * only needs the teaser, and for a locked lesson the server returns those as
 * null/[] anyway (spec 0018 §4 — the server is the gate, not the client).
 *
 * `isOpened` / `isBookmarked` (spec 0020 Фаза B/C, per-viewer engagement) drive
 * the row markers — a «просмотрен» eye for an opened-but-not-completed lesson and
 * a ★ for a bookmarked one. They never affect the unlock chain.
 */
export const CareerLessonsListQuery = gql`
  query CareerLessonsList {
    careerLessons {
      id
      order
      title
      summary
      durationMin
      completionKind
      isUnlocked
      isCompleted
      completedAt
      isOpened
      isBookmarked
    }
  }
`;

/**
 * A single lesson including the gated payload. When `isUnlocked === false` the
 * server returns `content: null`, `videoUrl: null`, `attachments: []` — the UI
 * renders the lock from `isUnlocked` alone and never guesses at unlock rules.
 *
 * Spec 0020 Фаза B/C per-viewer engagement:
 *  - `isOpened` / `openedAt` / `openCount` — «просмотрен» state (distinct from
 *    completed; opening never unlocks the next lesson).
 *  - `myNotes` — the student's PRIVATE notes; server returns them only on the
 *    LMS annotation, so staff never see them (spec 0020 §3.3).
 *  - `isBookmarked` — the ☆/★ toggle.
 *  - `quizQuestions.correctIndexes` is ALWAYS `[]` here — the answer key stays
 *    server-side and is only revealed in the `submitCareerQuiz` result.
 */
export const CareerLessonDetailQuery = gql`
  query CareerLessonDetail($id: ID!) {
    careerLesson(id: $id) {
      id
      order
      title
      summary
      durationMin
      completionKind
      artifactHint
      isUnlocked
      isCompleted
      completedAt
      isOpened
      openedAt
      openCount
      # ТЗ 0039 доводка UX: «этот урок прошли N» — социальное доказательство.
      # null ниже порога: маленькое число отговаривает сильнее, чем молчание.
      peersCompleted
      myNotes
      isBookmarked
      content
      videoUrl
      attachments {
        id
        originalName
        contentType
        sizeBytes
        url
      }
      quizQuestions {
        id
        order
        text
        options
        correctIndexes
      }
      # ТЗ 0039 — анкета-упражнение. На закрытом уроке сервер отдаёт null:
      # вопросы и рекомендации это тело урока, а не оформление.
      exercise {
        id
        intro
        outro
        fields {
          id
          number
          label
          helpText
          placeholder
          fieldType
          required
          options
          maxLength
          binding
          bindingLanguage
          prefill
        }
        mySubmission {
          id
          submittedAt
          answers {
            fieldId
            value
          }
        }
      }
    }
  }
`;

/**
 * The member's vacancy feed (spec 0018, increment B2). Operation name
 * `MyCareerVacancies` is deliberately distinct from the backend field
 * `careerVacancies` — equal names collapse the generated result type
 * (graphql-codegen skill).
 *
 * Returns `[]` for a non-member — the backend gates the same way as
 * `careerLessons`, answering with an empty list rather than an error, and
 * yields only PUBLISHED, unexpired rows.
 *
 * `myInteraction` is the caller's own state ('saved' | 'applied' | null); the
 * feed reads it to render the Save / Apply buttons. There is no LMS directions
 * query, so the direction filter's options are derived from the feed's own
 * `directionIds` / `directionTitles` (see useCareerVacancies).
 */
export const MyCareerVacanciesQuery = gql`
  query MyCareerVacancies(
    $directionId: ID
    $skillId: ID
    $workMode: String
    $seniority: String
    $search: String
    $savedOnly: Boolean
  ) {
    careerVacancies(
      directionId: $directionId
      skillId: $skillId
      workMode: $workMode
      seniority: $seniority
      search: $search
      savedOnly: $savedOnly
    ) {
      id
      title
      companyName
      applyUrl
      location
      workMode
      seniority
      employmentType
      salaryMin
      salaryMax
      salaryCurrency
      description
      publishedAt
      expiresAt
      directionIds
      directionTitles
      skillNames
      myInteraction
    }
  }
`;

/**
 * The ranked, paged feed (spec 0035). Replaces `MyCareerVacancies` for the
 * list; that query is KEPT so nothing breaks mid-migration.
 *
 * Two things the old feed could not do and this one must:
 *
 * * **Rank against the caller's own profile.** `relevanceApplied` is the
 *   honesty flag — false when the member has no profile signal, and the UI
 *   must then NOT claim «подобрано для вас» over what is really a date sort.
 * * **Page under head inserts.** The cursor is keyset, because the nightly
 *   agent publishes into the TOP of the order exactly while a student scrolls;
 *   an offset cursor duplicates and skips there.
 *
 * `descriptionShort` instead of `description`: the card clamps to three lines,
 * so shipping 4000 chars per row was pure waste. The full text lives on
 * `MyCareerVacancy` below.
 */
export const MyCareerVacancyFeedQuery = gql`
  query MyCareerVacancyFeed(
    $directionId: ID
    $skillIds: [ID!]
    $cityId: ID
    $workMode: String
    $seniority: String
    $employmentType: String
    $salaryFrom: Int
    $freshness: String
    $search: String
    $savedOnly: Boolean
    $sort: String
    $first: Int
    $after: String
  ) {
    careerVacancyFeed(
      directionId: $directionId
      skillIds: $skillIds
      cityId: $cityId
      workMode: $workMode
      seniority: $seniority
      employmentType: $employmentType
      salaryFrom: $salaryFrom
      freshness: $freshness
      search: $search
      savedOnly: $savedOnly
      sort: $sort
      first: $first
      after: $after
    ) {
      totalCount
      totalCountCapped
      relevanceApplied
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        cursor
        node {
          id
          title
          companyName
          applyUrl
          location
          cityId
          cityName
          workMode
          seniority
          employmentType
          salaryMin
          salaryMax
          salaryCurrency
          descriptionShort
          publishedAt
          expiresAt
          isNew
          directionIds
          directionTitles
          skillIds
          skillNames
          # Объектом — ради slug, по которому подбирается знак технологии,
          # и iconUrl. Плоские списки остаются: на них стоит фильтр по
          # навыку и подсветка совпадений. (Без обратных кавычек: комментарий
          # внутри gql-литерала, они оборвали бы шаблонную строку.)
          skills {
            id
            name
            slug
            category
            iconUrl
          }
          matchedSkillCount
          matchedSkillNames
          myInteraction
        }
      }
    }
  }
`;

/**
 * One vacancy, by id — what the details drawer reads (spec 0035).
 *
 * The drawer used to pick its row out of the loaded list, which meant a
 * background `cache-and-network` refetch or a filter change made the row
 * vanish and the drawer close mid-read. It also cannot work under pagination,
 * where the row may not be on the loaded page, and it is the only place the
 * FULL description now exists.
 *
 * The backend applies the same PUBLISHED + unexpired filter as the feed, so a
 * guessed id cannot reach a draft.
 */
export const MyCareerVacancyQuery = gql`
  query MyCareerVacancy($id: ID!) {
    careerVacancy(id: $id) {
      id
      title
      companyName
      applyUrl
      location
      cityName
      workMode
      seniority
      employmentType
      salaryMin
      salaryMax
      salaryCurrency
      description
      publishedAt
      expiresAt
      isNew
      directionTitles
      skillIds
      skillNames
      skills {
        id
        name
        slug
        category
        iconUrl
      }
      matchedSkillNames
      myInteraction
    }
  }
`;

/**
 * Dictionaries for the feed filters (spec 0035).
 *
 * The direction list used to be scraped out of the feed's own rows and
 * accumulated in a ref that never reset — so an empty first page left the
 * student with no direction filter at all, and the options could list
 * directions no longer present. A real dictionary has neither problem.
 */
export const MyCareerFilterDictionariesQuery = gql`
  query MyCareerFilterDictionaries {
    careerDirectionsLms {
      id
      title
    }
    careerCities {
      id
      name
      country
    }
  }
`;

/**
 * ТЗ 0039 шаг 4 — экран «Мой рост».
 *
 * Открыт ЛЮБОМУ студенту, а не только участнику центра, поэтому запрос
 * отдельный и в `careerCenter` не вложен: там гейт членства.
 */
export const MyGrowthQuery = gql`
  query MyGrowth {
    myGrowth {
      xp
      level
      levelTitle
      levelFloorXp
      nextLevelXp
      levels {
        index
        title
        threshold
        reached
      }
      availableTotal
      lockedTotal
      isCareerMember
      achievements {
        code
        title
        description
        icon
        category
        points
        earned
        earnedAt
        current
        target
        locked
        hidden
        progressKind
      }
      tracks {
        code
        title
        description
        icon
        chosen
        stepsDone
        stepsTotal
        steps {
          title
          description
          satisfied
          current
          target
          progressKind
        }
        missingSkills {
          id
          name
          slug
        }
        recommendedCourses {
          id
          name
          url
        }
      }
    }
  }
`;

/**
 * ТЗ 0039 шаг 5 — карта навыков.
 *
 * ⚠️ `demandComputedAt` запрашивается не для красоты: счётчик вакансий без
 * даты выглядит живым, даже когда ночная задача не отрабатывала неделю.
 */
export const MySkillMapQuery = gql`
  query MySkillMap {
    mySkillMap {
      trackCode
      trackTitle
      demandComputedAt
      nextCourses {
        courseId
        courseName
        courseUrl
        adds
        covered
        totalMissing
      }
      mine {
        level
        source
        isHidden
        grantedCourseName
        grantedLessonNumber
        skill {
          id
          name
          slug
          category
          description
          iconUrl
        }
        demand {
          liveVacancies
          band
        }
        providers {
          courseId
          courseName
          courseUrl
          level
          completed
        }
      }
      missing {
        # ТЗ 0040 доводка: если навык даёт курс, где студент УЖЕ учится,
        # честный ответ — «откроется на занятии N», а не «купите курс».
        upcoming {
          lessonNumber
          lessonsLeft
        }
        skill {
          id
          name
          slug
          category
          description
          iconUrl
        }
        demand {
          liveVacancies
          band
        }
        providers {
          courseId
          courseName
          courseUrl
          level
          completed
        }
      }
    }
  }
`;

/** ТЗ 0039 шаг 6 — мои промокоды. Отозванных и просроченных здесь нет. */
export const MyPromoCodesQuery = gql`
  query MyPromoCodes {
    myPromoCodes {
      id
      code
      status
      expiresAt
      campaignTitle
      campaignDescription
      discountKind
      discountValue
      currency
    }
  }
`;

/**
 * ТЗ 0039 шаг 7 — варианты репоста.
 *
 * `shareUrl` у Instagram приходит `null` — веб-эндпоинта шеринга у него нет.
 * Текст отдаётся отдельно, потому что положить его в окно шеринга нельзя ни в
 * одном канале: LinkedIn принимает только `url`, Facebook — только `u`.
 */
export const MyShareOptionsQuery = gql`
  query MyShareOptions($language: String) {
    myShareOptions(language: $language) {
      channel
      text
      targetUrl
      shareUrl
      bonusAvailable
      bonusPoints
      bonusPromo
    }
  }
`;

/**
 * ТЗ 0039, доводка UX — справочник профессий во вкладке «Мой рост».
 *
 * Отдельным запросом от `MyGrowth`: вкладка открывается не всегда, а список
 * считает вакансии по доске и цели по каждому треку. Подмешивать это в
 * `MyGrowth` значило бы платить за него на каждом заходе в раздел.
 */
export const CareerProfessionsQuery = gql`
  query CareerProfessions {
    careerProfessions {
      code
      title
      summary
      about
      icon
      salaryFrom
      salaryTo
      salaryCurrency
      haveCount
      totalCount
      liveVacancies
      skills {
        id
        name
        slug
        category
        iconUrl
        have
      }
      courses {
        courseId
        courseName
        courseUrl
        completed
      }
    }
  }
`;

/**
 * ТЗ 0039, доводка UX — один навык на СВОЕЙ странице.
 *
 * Заменяет выдвижную шторку карты навыков: её нельзя ни дать ссылкой, ни
 * открыть с другого экрана, ни вернуть кнопкой «назад». Владелец: «кликаешь на
 * умение, открывается справа поп-ап — не очевидно».
 */
export const CareerSkillDetailQuery = gql`
  query CareerSkillDetail($slug: String!) {
    careerSkillDetail(slug: $slug) {
      myLevel
      mySource
      grantedCourseName
      grantedLessonNumber
      skill {
        id
        name
        slug
        category
        description
        iconUrl
      }
      demand {
        liveVacancies
        band
      }
      providers {
        courseId
        courseName
        courseUrl
        level
        completed
      }
      professions {
        code
        title
        summary
        haveCount
        totalCount
      }
    }
  }
`;
